import { Box } from '@mui/joy';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { PaymentAlarmProvider, PaymentFailAlarmProvider } from '@MP/features/payment';
import { ScanModuleProvider } from '@MP/features/scan';
import {
  useFetchCurrentUser,
  useFetchOrganizationOfCurrentUser,
  useIsOrganizationUsingStim,
} from '@MP/shared/api/hooks/auth';
import { useFetchNotice } from '@MP/shared/api/hooks/notice';
import { useHasCardInfo } from '@MP/shared/api/hooks/payhub';
import { Notice } from '@MP/shared/api/types';
import ChannelTalkLauncher from '@MP/shared/components/ChannelTalkLauncher';
import { CHANNEL_TALK_WORKFLOW_ID, useChannelTalk } from '@MP/shared/hooks/useChannelTalk';
import { useClarityUser } from '@MP/shared/hooks/useClarityUser';
import useLogout from '@MP/shared/hooks/useLogout';
import FeedSocketProvider from '@MP/shared/providers/FeedSocketProvider';
import PatientProvider from '@MP/shared/providers/PatientProvider';
import { filterVisibleNotices, useGlobalUIStore } from '@MP/shared/stores/GlobalUIStore';

import { NoticeProvider } from '../providers/NoticeProvider';

import Sidebar from './Sidebar';

const MainLayout = () => {
  const setHideNotice = useGlobalUIStore((s) => s.setHideNotice);
  const { data: isUsingStim } = useIsOrganizationUsingStim();
  const { data: hasCardInfo } = useHasCardInfo();
  const { data: currentUser, isError } = useFetchCurrentUser();
  const { logout } = useLogout();
  const { data: organization } = useFetchOrganizationOfCurrentUser();

  useClarityUser();
  const { openWorkflow, isBooted } = useChannelTalk({
    memberId: currentUser?.id,
    name: organization?.name,
    tel: currentUser?.tel,
    isHospital: true,
    workflowId: CHANNEL_TALK_WORKFLOW_ID.WEB,
  });

  useEffect(() => {
    window.MinddPlugin.setOrgName(organization?.name);
  }, [organization]);

  useEffect(() => {
    if (isError) {
      logout();
    }
  }, [logout, isError]);

  const { data: noticeData } = useFetchNotice();
  const notices: Notice[] =
    isUsingStim !== undefined && hasCardInfo !== undefined && noticeData
      ? filterVisibleNotices(noticeData, true, hasCardInfo, isUsingStim)
      : [];

  return (
    <FeedSocketProvider>
      <PatientProvider>
        <NoticeProvider notices={notices} onClose={(n, o) => setHideNotice(n, o)} />
        <ScanModuleProvider>
          <Box height={'100vh'}>
            <Sidebar />
            <Box sx={{ ml: '70px', height: '100%', bgcolor: '#F5F5F5' }}>
              <Outlet />
            </Box>
          </Box>
        </ScanModuleProvider>
        <PaymentAlarmProvider />
        <PaymentFailAlarmProvider />
        {isBooted && <ChannelTalkLauncher onClick={openWorkflow} />}
      </PatientProvider>
    </FeedSocketProvider>
  );
};

export { MainLayout };
